import { useState, useEffect, useMemo, useCallback } from 'react';
import SubLayout from '@/components/feature/SubLayout';
import { useSubcontractorAssignments } from '@/hooks/useSubcontractorAssignments';
import { useDailyLog, type DailyLogPhoto } from '@/hooks/useDailyLog';
import { DAILY_LOG_SYNC_EVENT } from '@/lib/dailyLogUtils';
import { useDailyLogStageSchedule } from '@/hooks/useDailyLogStageSchedule';
import { useProjectSummary } from '@/hooks/useProjectSummary';
import ProjectPhotoImage from '@/components/base/ProjectPhotoImage';
import DayDetailPanel from './components/DayDetailPanel';
import { useAuth } from '@/hooks/useAuth';
import { type ScheduleSettings, type HolidayInfo } from '@/lib/scheduleUtils';
import { isProjectNonWorkingDay } from '@/lib/workingDays';
import { useSignedFileUrls } from '@/lib/storageSignedUrl';
import { supabase } from '@/lib/supabase';
import { useTranslation } from 'react-i18next';

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function formatDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}

function parseDateKey(s: string): Date {
  return new Date(`${s}T00:00:00`);
}

interface CalendarDayInfo {
  stages: string[];
  photos: DailyLogPhoto[];
  count: number;
  logCount: number;
  hasSubmittedLog: boolean;
  status: { color: string; label: string } | null;
}

export default function SubcontractorDailyLog() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { projects, projectIds, loading: projectsLoading } = useSubcontractorAssignments();

  const MONTH_LABELS = [
    t('sub.calendar.january'), t('sub.calendar.february'), t('sub.calendar.march'),
    t('sub.calendar.april'), t('sub.calendar.may'), t('sub.calendar.june'),
    t('sub.calendar.july'), t('sub.calendar.august'), t('sub.calendar.september'),
    t('sub.calendar.october'), t('sub.calendar.november'), t('sub.calendar.december'),
  ];

  const WEEKDAY_LABELS = [
    t('sub.calendar.sun'), t('sub.calendar.mon'), t('sub.calendar.tue'),
    t('sub.calendar.wed'), t('sub.calendar.thu'), t('sub.calendar.fri'), t('sub.calendar.sat'),
  ];

  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);

  const effectiveProjectIds = useMemo(() => {
    if (!selectedProjectId) return [];
    return [selectedProjectId];
  }, [selectedProjectId]);

  const { photos, daySummaries, loading: photosLoading, error: dailyLogError, refresh } = useDailyLog(
    effectiveProjectIds,
    { userId: user?.id ?? null }
  );
  const photoSignedUrls = useSignedFileUrls(photos);
  const { stageSchedules, getMatchingStagesForDate, getStageForDate, refresh: refreshStageSchedules } = useDailyLogStageSchedule(projectIds);

  // Live project summary for the currently selected single project
  const singleProjectId = selectedProjectId || undefined;
  const projectSummary = useProjectSummary(singleProjectId);

  // Resolve initial project selection: URL param > localStorage > first assigned
  useEffect(() => {
    if (projectsLoading || projects.length === 0) return;
    if (selectedProjectId) return; // already selected

    const STORAGE_KEY = 'jph_subcontractor_daily_log_project_id';
    const urlParams = new URLSearchParams(window.location.search);
    const urlProjectId = urlParams.get('projectId');

    let chosenId: string | null = null;
    let source = '';

    if (urlProjectId && projects.some((p) => p.id === urlProjectId)) {
      chosenId = urlProjectId;
      source = 'url';
    } else {
      const storedId = localStorage.getItem(STORAGE_KEY);
      if (storedId && projects.some((p) => p.id === storedId)) {
        chosenId = storedId;
        source = 'localStorage';
      } else {
        chosenId = projects[0].id;
        source = 'default-first';
      }
    }

    if (chosenId) {
      setSelectedProjectId(chosenId);
      console.log('Subcontractor Daily Log selected project', {
        selectedProjectId: chosenId,
        selectedProject: projects.find((p) => p.id === chosenId)?.project_name,
        source,
      });
    }
  }, [projectsLoading, projects, selectedProjectId]);

  // Persist selection to localStorage
  useEffect(() => {
    if (!selectedProjectId) return;
    const STORAGE_KEY = 'jph_subcontractor_daily_log_project_id';
    localStorage.setItem(STORAGE_KEY, selectedProjectId);
  }, [selectedProjectId]);

  // Debug: log assigned projects
  useEffect(() => {
    console.log('Subcontractor Daily Log assigned projects', {
      count: projects.length,
      projects: projects.map((p) => ({ id: p.id, name: p.project_name })),
    });
  }, [projects]);

  // Debug: log load state for selected project
  useEffect(() => {
    const proj = projects.find((p) => p.id === selectedProjectId);
    console.log('Daily Log load for selected project', {
      projectId: selectedProjectId,
      projectName: proj?.project_name,
      logsCount: Object.keys(daySummaries ?? {}).length,
      photosCount: photos.length,
      photosLoading,
    });
  }, [selectedProjectId, projects, daySummaries, photos, photosLoading]);

  useEffect(() => {
    if (!singleProjectId || !projectSummary.loaded) return;
    const selectedProject = projects.find((p) => p.id === singleProjectId);
    console.log('Subcontractor Daily Log project status debug', {
      projectId: singleProjectId,
      rawProjectStatus: selectedProject?.status,
      rawCurrentStage: selectedProject?.current_stage,
      summaryProjectStatus: projectSummary.projectStatus,
      summaryActiveStage: projectSummary.activeStage,
      summaryProjectProgress: projectSummary.projectProgress,
    });
  }, [singleProjectId, projectSummary.loaded, projectSummary.projectStatus, projectSummary.activeStage, projectSummary.projectProgress, projects]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ projectId?: string }>).detail;
      if (detail?.projectId && selectedProjectId && detail.projectId !== selectedProjectId) return;
      refresh();
      refreshStageSchedules();
    };
    window.addEventListener(DAILY_LOG_SYNC_EVENT, handler);
    return () => window.removeEventListener(DAILY_LOG_SYNC_EVENT, handler);
  }, [selectedProjectId, refresh, refreshStageSchedules]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ projectId: string }>).detail;
      if (!selectedProjectId || detail?.projectId === selectedProjectId) {
        refresh();
        refreshStageSchedules();
      }
    };
    window.addEventListener('__jphph_schedule_sync__', handler);
    return () => window.removeEventListener('__jphph_schedule_sync__', handler);
  }, [selectedProjectId, refresh, refreshStageSchedules]);

  useEffect(() => {
    const handler = () => {
      if (!document.hidden) {
        refresh();
        refreshStageSchedules();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [refresh, refreshStageSchedules]);

  const [scheduleSettings, setScheduleSettings] = useState<Record<string, { settings: ScheduleSettings; holidays: HolidayInfo[]; projectHolidays: HolidayInfo[] }>>();
  const [settingsLoading, setSettingsLoading] = useState(false);

  useEffect(() => {
    if (projectIds.length === 0) return;
    setSettingsLoading(true);
    (async () => {
      const map: Record<string, { settings: ScheduleSettings; holidays: HolidayInfo[]; projectHolidays: HolidayInfo[] }> = {};
      for (const pid of projectIds) {
        const { data: settingsData } = await supabase
          .from('jphph_project_schedule_settings')
          .select('*')
          .eq('project_id', pid)
          .maybeSingle();

        const settings = settingsData as ScheduleSettings | null;
        let holidays: HolidayInfo[] = [];
        let projectHolidays: HolidayInfo[] = [];

        if (settings) {
          const country = settings.holiday_country || 'NZ';
          const [{ data: pubData }, { data: projData }] = await Promise.all([
            supabase.from('jphph_schedule_holidays').select('holiday_date, name, holiday_name').eq('country_code', country).eq('is_active', true),
            supabase.from('jphph_project_schedule_holidays').select('holiday_date, name, holiday_name').eq('project_id', pid).eq('is_active', true),
          ]);
          holidays = (pubData || []).map((h: Record<string, unknown>) => ({ date: String(h.holiday_date), name: String(h.holiday_name || h.name || 'Holiday') }));
          projectHolidays = (projData || []).map((h: Record<string, unknown>) => ({ date: String(h.holiday_date), name: String(h.holiday_name || h.name || 'Holiday') }));
        }

        map[pid] = { settings: settings || { working_days: [1,2,3,4,5,6], sunday_off: true, saturday_off: true } as ScheduleSettings, holidays, projectHolidays };
      }
      setScheduleSettings(map);
      setSettingsLoading(false);
    })();
  }, [projectIds.join(',')]);

  // Helper: is a day non-working for the currently selected project?
  const isNonWorking = useCallback(
    (date: Date) => {
      if (!scheduleSettings || !selectedProjectId) return false;
      const cfg = scheduleSettings[selectedProjectId];
      return isProjectNonWorkingDay(date, cfg?.settings || null, cfg?.holidays || [], cfg?.projectHolidays || []).isNonWorking;
    },
    [selectedProjectId, scheduleSettings]
  );

  const getNonWorkingLabel = useCallback(
    (date: Date) => {
      if (!scheduleSettings || !selectedProjectId) return null;
      const cfg = scheduleSettings[selectedProjectId];
      return isProjectNonWorkingDay(date, cfg?.settings || null, cfg?.holidays || [], cfg?.projectHolidays || []).label;
    },
    [selectedProjectId, scheduleSettings]
  );

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const calendarDays = useMemo(() => {
    const cells: { date: Date; key: string }[] = [];
    for (let i = 0; i < firstDay; i++) cells.push({ date: new Date(year, month, i - firstDay + 1), key: '' });
    for (let i = 1; i <= daysInMonth; i++) cells.push({ date: new Date(year, month, i), key: formatDateKey(new Date(year, month, i)) });
    const rem = cells.length % 7;
    if (rem > 0) for (let i = 1; i <= 7 - rem; i++) cells.push({ date: new Date(year, month + 1, i), key: '' });
    return cells;
  }, [year, month, firstDay, daysInMonth]);

  // Build combined calendar data: schedule stages + photos per day
  // For non-working days, skip stage detection entirely.
  const calendarData = useMemo<Record<string, CalendarDayInfo>>(() => {
    const data: Record<string, CalendarDayInfo> = {};
    const targetIds = selectedProjectId ? [selectedProjectId] : [];

    // Build from schedule first (only for working days)
    targetIds.forEach((pid) => {
      const stages = stageSchedules[pid] ?? [];
      stages.forEach((stage) => {
        if (!stage.start_date || !stage.end_date) return;
        const start = new Date(stage.start_date + 'T00:00:00');
        const end = new Date(stage.end_date + 'T00:00:00');
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const key = formatDateKey(d);
          // Skip non-working days: do not show stage badges
          if (isNonWorking(d)) continue;
          if (!data[key]) data[key] = { stages: [], photos: [], count: 0, logCount: 0, hasSubmittedLog: false, status: null };
          if (!data[key].stages.includes(stage.stage_name)) {
            data[key].stages.push(stage.stage_name);
          }
        }
      });
    });

    // Merge photo + daily log entry data
    Object.entries(daySummaries ?? {}).forEach(([key, summary]) => {
      if (!data[key]) data[key] = { stages: [], photos: [], count: 0, logCount: 0, hasSubmittedLog: false, status: null };
      data[key].photos = summary.photos;
      data[key].count = summary.count;
      data[key].logCount = summary.logCount;
      data[key].hasSubmittedLog = summary.hasSubmittedLog;

      summary.photos.forEach((p) => {
        if (p.stage_name && !data[key].stages.includes(p.stage_name)) {
          data[key].stages.push(p.stage_name);
        }
      });

      const photos = summary.photos;
      const hasPending = photos.some((p) => p.review_status === 'pending_management_review');
      const hasApproved = photos.some((p) => p.review_status === 'approved_for_client');
      const hasRejected = photos.some((p) => p.review_status === 'rejected');
      const hasInternal = photos.some((p) => p.review_status === 'internal_only');

      if (summary.hasSubmittedLog) {
        data[key].status = { color: '#059669', label: t('sub.dailyLog.submittedBadge') };
      } else if (hasPending) data[key].status = { color: '#F97316', label: t('sub.dailyLog.pendingReview') };
      else if (hasApproved) data[key].status = { color: '#059669', label: t('sub.dailyLog.approved') };
      else if (hasRejected) data[key].status = { color: '#EF4444', label: t('sub.dailyLog.rejected') };
      else if (hasInternal) data[key].status = { color: '#374151', label: t('sub.dailyLog.internalOnly') };
      else if (photos.length > 0) data[key].status = { color: '#9CA3AF', label: t('sub.dailyLog.mixed') };
    });

    // Development diagnostics: log combined data sources
    if (import.meta.env.DEV) {
      const allRawStages = targetIds.flatMap((pid) => stageSchedules[pid] ?? []);
      const normalizedStages = Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v.stages])
      );
      const photosForDate = Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, { count: v.count, photoIds: v.photos.map((p) => p.id) }])
      );
      console.log({
        context: 'Daily Log Calendar',
        selectedProjectId,
        assignedProjectIds: projectIds,
        month: `${year}-${String(month + 1).padStart(2, '0')}`,
        rawStages: allRawStages.map((s) => ({
          id: s.id,
          name: s.stage_name,
          start: s.start_date,
          end: s.end_date,
          status: s.status,
        })),
        normalizedStages,
        detectedStagesForDate: normalizedStages,
        photosForDate,
      });
    }

    return data;
  }, [daySummaries, stageSchedules, selectedProjectId, isNonWorking, t]);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  function getDayStatusDot(info: CalendarDayInfo | undefined): { color: string; label: string } | null {
    if (!info) return null;
    if (info.hasSubmittedLog) return info.status ?? { color: '#059669', label: t('sub.dailyLog.submittedBadge') };
    if (info.count === 0) return null;
    return info.status;
  }

  function renderDayActivityBadge(info: CalendarDayInfo | undefined) {
    if (!info) return null;
    const parts: string[] = [];
    if (info.hasSubmittedLog) {
      parts.push(info.logCount > 1 ? t('sub.dailyLog.logCountBadge', { count: info.logCount }) : t('sub.dailyLog.submittedBadge'));
    }
    if (info.count > 0) {
      parts.push(info.count > 1 ? t('sub.dailyLog.photoCountBadge', { count: info.count }) : t('sub.dailyLog.onePhotoBadge'));
    }
    if (parts.length === 0) return null;
    return (
      <span className="text-[10px] font-bold text-[#059669] bg-[#059669]/10 px-1.5 py-0.5 rounded-full flex-shrink-0 truncate max-w-full">
        {parts.join(' · ')}
      </span>
    );
  }

  function renderDayCell(cell: { date: Date; key: string }) {
    const isCurrentMonth = cell.date.getMonth() === month;
    if (!isCurrentMonth) return <div key={`pad-${cell.date.getTime()}`} className="aspect-square border border-gray-50 bg-gray-50/50 rounded-xl" />;

    const key = formatDateKey(cell.date);
    const info = calendarData[key];
    const isToday = key === formatDateKey(new Date());
    const isSelected = key === selectedDateKey;
    const hasPhotos = (info?.count ?? 0) > 0;
    const hasSubmittedLog = info?.hasSubmittedLog ?? false;
    const hasActivity = hasPhotos || hasSubmittedLog;
    const statusDot = getDayStatusDot(info);
    const nonWorking = isNonWorking(cell.date);
    const nwLabel = nonWorking ? getNonWorkingLabel(cell.date) : null;

    // ── Non-working day cell ──
    if (nonWorking) {
      return (
        <button
          key={key}
          onClick={() => setSelectedDateKey(key)}
          className={`aspect-square min-h-[120px] border rounded-xl p-2 flex flex-col items-start justify-start transition-all cursor-pointer text-left overflow-hidden box-border ${
            isSelected
              ? 'border-[#059669] bg-slate-100'
              : 'border-slate-200 bg-slate-100'
          }`}
        >
          <div className="w-full flex items-center justify-between min-w-0">
            <span
              className={`text-xs font-semibold ${
                isToday
                  ? 'bg-[#059669] text-white w-6 h-6 flex items-center justify-center rounded-full'
                  : 'text-slate-400'
              }`}
            >
              {cell.date.getDate()}
            </span>
            {renderDayActivityBadge(info)}
          </div>

          {/* Non-workday label */}
          {nwLabel && (
            <div className="w-full mt-auto mb-0.5 text-center min-w-0">
              <span className="inline-block px-1.5 py-0.5 bg-slate-200 text-slate-500 text-[7px] font-bold rounded-full truncate max-w-full">
                {nwLabel}
              </span>
            </div>
          )}

          {/* Photo thumbnails (existing photos remain visible but clipped) */}
          {hasPhotos && info && (
            <div className="w-full mt-2 flex gap-1 max-w-full overflow-hidden min-w-0">
              {info.photos.slice(0, 2).map((p) => (
                <div key={p.id} className="h-10 w-10 flex-shrink-0 rounded-md overflow-hidden bg-slate-200">
                  <img
                    src={photoSignedUrls[p.id] || p.file_url}
                    alt=""
                    className="w-full h-full object-cover object-center"
                    loading="lazy"
                  />
                </div>
              ))}
              {info.photos.length > 2 && (
                <div className="h-10 w-10 flex-shrink-0 rounded-md bg-slate-300 flex items-center justify-center">
                  <span className="text-[9px] text-slate-500 font-bold">+{info.photos.length - 2}</span>
                </div>
              )}
            </div>
          )}

          {statusDot && (
            <div className="w-full flex justify-center mt-1">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusDot.color }} title={statusDot.label} />
            </div>
          )}
        </button>
      );
    }

    // ── Working day cell ──
    const hasSchedule = (info?.stages.length ?? 0) > 0;

    // DEBUG: log the source of the calendar label for every working day cell
    if (import.meta.env.DEV) {
      const label = projectSummary.loaded && projectSummary.projectProgress === 100
        ? 'Completed'
        : (hasSchedule ? info!.stages[0] : null);
      const labelType = projectSummary.loaded && projectSummary.projectProgress === 100
        ? 'project_status_override'
        : (hasSchedule ? 'schedule_stage_historical' : 'no_stage');
      // eslint-disable-next-line no-console
      console.log('Daily Log calendar entry status source', {
        date: key,
        label,
        labelType,
        source: projectSummary.loaded && projectSummary.projectProgress === 100 ? 'useProjectSummary' : 'useDailyLogStageSchedule',
        scheduleItemId: hasSchedule ? 'derived_from_stage_schedule' : null,
        stageName: hasSchedule ? info!.stages[0] : null,
        projectStatus: projectSummary.loaded ? projectSummary.projectStatus : 'loading',
        projectProgress: projectSummary.loaded ? projectSummary.projectProgress : null,
        isProjectComplete: projectSummary.loaded && projectSummary.projectProgress === 100,
      });
    }

    // Dev diagnostic for 11 May 2026
    if (import.meta.env.DEV && key === '2026-05-11') {
      console.log({
        context: 'Daily Log Calendar — 11 May 2026 cell',
        selectedProjectId,
        assignedProjectIds: projectIds,
        date: key,
        isNonWorkingDay: nonWorking,
        detectedStage: hasSchedule ? info!.stages[0] : null,
        allStagesForDate: info?.stages ?? [],
        photosCount: info?.count ?? 0,
        thumbnailUrls: info?.photos.slice(0, 3).map((p) => p.file_url),
        photoDetails: info?.photos.map((p) => ({
          id: p.id,
          fileName: p.file_name,
          stageId: p.stage_id,
          stageName: p.stage_name,
        })),
      });
    }

    return (
      <button
        key={key}
        onClick={() => setSelectedDateKey(key)}
        className={`aspect-square min-h-[120px] border rounded-xl p-2 flex flex-col items-start justify-start transition-all cursor-pointer text-left overflow-hidden box-border ${
          isSelected
            ? 'border-[#059669] bg-[#059669]/5'
            : hasActivity
              ? 'border-[#E5E2DC] bg-white hover:bg-gray-50'
              : 'border-gray-100 bg-white hover:bg-gray-50'
        }`}
      >
        <div className="w-full flex items-center justify-between min-w-0 gap-1">
          <span
            className={`text-xs font-semibold ${
              isToday
                ? 'bg-[#059669] text-white w-6 h-6 flex items-center justify-center rounded-full'
                : isSelected
                  ? 'text-[#059669]'
                  : 'text-[#374151]'
            }`}
          >
            {cell.date.getDate()}
          </span>
          {renderDayActivityBadge(info)}
        </div>

        {/* Scheduled stage — only when no submitted log for this day */}
        {!hasSubmittedLog && hasSchedule && (
          <div className="w-full mt-auto mb-0.5 min-w-0">
            {info!.stages.length > 1 ? (
              <span
                className="inline-block px-1.5 py-0.5 bg-[#FEF0E7] text-[#E8621A] text-[8px] font-bold rounded-full truncate max-w-full"
                title={info!.stages.join(', ')}
              >
                {info!.stages[0]} +{info!.stages.length - 1}
              </span>
            ) : (
              <span
                className="inline-block px-1.5 py-0.5 bg-[#FEF0E7] text-[#E8621A] text-[8px] font-bold rounded-full truncate max-w-full"
                title={info!.stages[0]}
              >
                {info!.stages[0]}
              </span>
            )}
          </div>
        )}

        {/* Photo thumbnails — clipped inside cell */}
        {hasPhotos && info && (
          <div className="w-full mt-1 flex gap-1 max-w-full overflow-hidden min-w-0">
            {info.photos.slice(0, 2).map((p) => (
              <div key={p.id} className="h-10 w-10 flex-shrink-0 rounded-md overflow-hidden bg-gray-100">
                <img
                  src={photoSignedUrls[p.id] || p.file_url}
                  alt=""
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                />
              </div>
            ))}
            {info.photos.length > 2 && (
              <div className="h-10 w-10 flex-shrink-0 rounded-md bg-gray-200 flex items-center justify-center">
                <span className="text-[9px] text-gray-500 font-bold">+{info.photos.length - 2}</span>
              </div>
            )}
          </div>
        )}

        {statusDot && (
          <div className="w-full flex justify-center mt-1">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusDot.color }} title={statusDot.label} />
          </div>
        )}
      </button>
    );
  }

  const selectedDateLabel = selectedDateKey
    ? parseDateKey(selectedDateKey).toLocaleDateString(t('date.locale') || 'en-NZ', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';

  const selectedProject = projects.find((p) => p.id === selectedProjectId);
  const selectedProjectName = selectedProject?.project_name ?? '';

  const loading = projectsLoading || photosLoading;

  const uploadDefaultProjectId = selectedProjectId;

  const savedLogForSelectedDay = useMemo(() => {
    if (!selectedDateKey || !daySummaries?.[selectedDateKey]) return null;
    const logs = daySummaries[selectedDateKey].logs;
    if (logs.length === 0) return null;
    if (selectedProjectId) {
      return logs.find((l) => l.project_id === selectedProjectId) ?? logs[0];
    }
    return logs[0];
  }, [selectedDateKey, selectedProjectId, daySummaries]);

  return (
    <SubLayout title={t('sub.dailyLog.title')}>
      <div className="px-4 lg:px-8 pt-4 pb-6">
        {/* Project selector — project-specific only, no All Projects */}
        <div className="mb-4">
          <p className="text-[#9CA3AF] text-xs font-semibold uppercase tracking-wider mb-2">{t('sub.dailyLog.project')}</p>
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {projects.map((proj) => (
              <button
                key={proj.id}
                onClick={() => {
                  setSelectedProjectId(proj.id);
                  setSelectedDateKey(null);
                  console.log('Subcontractor Daily Log project chip clicked', {
                    selectedProjectId: proj.id,
                    selectedProject: proj.project_name,
                    source: 'click',
                  });
                }}
                className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                  selectedProjectId === proj.id
                    ? 'bg-[#E8621A] text-white border border-[#E8621A] shadow-sm'
                    : 'bg-white border border-gray-200 text-[#6B7280] hover:border-[#E8621A] hover:text-[#E8621A]'
                }`}
              >
                {proj.project_name}
              </button>
            ))}
          </div>
        </div>

        {/* Selected project header — LIVE project summary from useProjectSummary */}
        {dailyLogError && (
          <div className="mb-4 p-4 bg-[#FEF2F2] border border-[#FECACA] rounded-xl text-sm text-[#EF4444] flex items-start gap-2">
            <i className="ri-error-warning-line mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">{t('sub.dailyLog.loadErrorTitle')}</p>
              <p className="text-xs mt-1">{dailyLogError}</p>
              <button
                type="button"
                onClick={() => refresh()}
                className="mt-2 text-xs font-bold text-[#EF4444] underline cursor-pointer"
              >
                {t('sub.dailyLog.retryLoad')}
              </button>
            </div>
          </div>
        )}

        {selectedProjectId && selectedProject && (
          <div className="mb-4 p-4 bg-white border border-gray-100 rounded-xl">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-[#1A1A1A] font-bold text-base">{selectedProject.project_name}</h2>
                <p className="text-[#9CA3AF] text-xs mt-0.5">{selectedProject.site_address || '—'}</p>

                {projectSummary.loaded ? (
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full capitalize"
                      style={{
                        backgroundColor: projectSummary.projectStatus === 'complete' ? '#F0FDF4' : '#FEF0E7',
                        color: projectSummary.projectStatus === 'complete' ? '#059669' : '#E8621A',
                      }}
                    >
                      {projectSummary.projectStatus === 'complete' ? t('sub.dailyLog.completedBadge') : projectSummary.projectStatus}
                    </span>
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: projectSummary.activeStage === 'Completed' ? '#F0FDF4' : '#FEF0E7',
                        color: projectSummary.activeStage === 'Completed' ? '#059669' : '#E8621A',
                      }}
                    >
                      {projectSummary.activeStage}
                    </span>
                    <span className="text-[10px] text-[#9CA3AF]">
                      {projectSummary.projectProgress}{t('sub.dailyLog.complete')}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[10px] text-[#9CA3AF]">{t('sub.dailyLog.loadingStatus')}</span>
                  </div>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-[#059669] font-bold text-sm">{photos.length}</div>
                <div className="text-[#9CA3AF] text-xs">{t('sub.dailyLog.noPhotos')}</div>
              </div>
            </div>

            {/* Detailed progress breakdown */}
            {projectSummary.loaded && (
              <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-100">
                <div className="text-center">
                  <div className="text-[#1A1A1A] font-bold text-sm">{projectSummary.projectProgress}%</div>
                  <div className="text-[#9CA3AF] text-[10px]">{t('sub.dailyLog.projectProgress')}</div>
                </div>
                <div className="text-center">
                  <div className="text-[#1A1A1A] font-bold text-sm">{projectSummary.scheduleFinalProgress}%</div>
                  <div className="text-[#9CA3AF] text-[10px]">{t('sub.dailyLog.scheduleFinal')}</div>
                </div>
                <div className="text-center">
                  <div className="text-[#1A1A1A] font-bold text-sm">{projectSummary.checklistFinalProgress}%</div>
                  <div className="text-[#9CA3AF] text-[10px]">{t('sub.dailyLog.checklistFinal')}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty state: projects exist but auto-selection failed or truly unselected */}
        {!selectedProjectId && !projectsLoading && projects.length > 0 && (
          <div className="text-center py-16 mb-6">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
              <i className="ri-folder-3-line text-5xl" />
            </div>
            <p className="text-[#374151] font-semibold mb-1">{t('sub.dailyLog.selectProjectTitle')}</p>
            <p className="text-[#9CA3AF] text-sm max-w-xs mx-auto">{t('sub.dailyLog.selectProjectDesc')}</p>
          </div>
        )}

        {/* Calendar — only show when a project is selected */}
        {selectedProjectId && (
          <>
            {/* Calendar header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <h2 className="text-[#1A1A1A] font-bold text-lg">{MONTH_LABELS[month]} {year}</h2>
                <button
                  onClick={() => setCurrentDate(new Date())}
                  className="px-3 py-1 text-xs font-semibold text-[#059669] border border-[#059669] rounded-full cursor-pointer hover:bg-[#059669]/5 transition-colors whitespace-nowrap"
                >
                  {t('sub.dailyLog.todayBtn')}
                </button>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={prevMonth}
                  className="w-8 h-8 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-[#374151] hover:bg-gray-50 cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-lg" />
                </button>
                <button
                  onClick={nextMonth}
                  className="w-8 h-8 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-[#374151] hover:bg-gray-50 cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-lg" />
                </button>
              </div>
            </div>

            {/* Weekday headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {WEEKDAY_LABELS.map((d) => (
                <div key={d} className="text-center text-[11px] font-bold text-[#9CA3AF] uppercase py-1">{d}</div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1 mb-6">
              {calendarDays.map((cell) => renderDayCell(cell))}
            </div>
          </>
        )}

        {/* No projects state */}
        {!projectsLoading && projects.length === 0 && (
          <div className="text-center py-16">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
              <i className="ri-folder-3-line text-5xl" />
            </div>
            <p className="text-[#374151] font-semibold mb-1">{t('sub.dailyLog.noAssignedProjectsLong')}</p>
            <p className="text-[#9CA3AF] text-sm max-w-xs mx-auto">{t('sub.dailyLog.noAssignedProjectsLongDesc')}</p>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <i className="ri-loader-4-line animate-spin text-[#E8621A] text-2xl" />
          </div>
        )}
      </div>

      {/* Day detail panel */}
      {selectedDateKey && user && (
        <DayDetailPanel
          dateKey={selectedDateKey}
          dateLabel={selectedDateLabel}
          photos={photos}
          savedLog={savedLogForSelectedDay}
          projects={projects}
          defaultProjectId={uploadDefaultProjectId}
          userId={user.id}
          getMatchingStagesForDate={getMatchingStagesForDate}
          getStageForDate={getStageForDate}
          isNonWorkingDay={isNonWorking(parseDateKey(selectedDateKey))}
          nonWorkingLabel={getNonWorkingLabel(parseDateKey(selectedDateKey)) ?? ''}
          onClose={() => setSelectedDateKey(null)}
          onUploadComplete={refresh}
        />
      )}
    </SubLayout>
  );
}